import React, { useEffect, useRef, useState } from 'react';
import Lightbox from './Lightbox';

// Fades and lifts its children into view the first time they scroll on screen
export const Reveal = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setShown(true); return undefined; }
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setShown(true); io.disconnect(); } },
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`transition-all duration-700 ease-out motion-reduce:transition-none ${shown ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0 motion-reduce:translate-y-0 motion-reduce:opacity-100'} ${className}`}
    >
      {children}
    </div>
  );
};

// Tilted logo-colour plates behind a framed slide. Slides glide sideways as you scroll.
export const ScrollFrame = ({ images, active = 0, onSelect }) => {
  const n = images.length;
  const [zoom, setZoom] = useState(false);
  return (
    <div
      className="relative mx-auto w-full px-4 py-5"
      style={{ maxWidth: 'min(620px, max(340px, calc(100vh - 230px)))' }}
    >
      <div
        className="absolute inset-0 rotate-[3deg] rounded-[28px] shadow-xl"
        style={{ background: 'linear-gradient(135deg, #f9d628 0%, #4bb214 100%)' }}
      />
      <div
        className="absolute inset-0 rotate-[-2deg] rounded-[28px] opacity-95"
        style={{ background: 'linear-gradient(135deg, #f9617f 0%, #1fb6e8 100%)' }}
      />
      <div className="relative rounded-2xl border-[6px] border-white bg-white shadow-2xl">
        <div
          className="relative aspect-[9/8] cursor-zoom-in overflow-hidden rounded-xl bg-slate-100"
          onClick={() => setZoom(true)}
          title="Click to view full size"
        >
          {images.map((im, i) => (
            <div
              key={i}
              aria-hidden={i !== active}
              style={{ transform: `translateX(${(i - active) * 100}%)` }}
              className={`absolute inset-0 flex flex-col bg-gradient-to-br from-slate-50 to-slate-200 transition-[transform,opacity] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none ${i === active ? 'opacity-100' : 'opacity-0'}`}
            >
              <div
                className="h-1 w-full shrink-0"
                style={{ background: 'linear-gradient(90deg, #f9617f, #1fb6e8, #f9d628, #4bb214)' }}
              />
              <div className="flex shrink-0 items-center justify-between px-4 pb-1 pt-2.5">
                <span className="truncate text-[13px] font-semibold tracking-tight text-slate-800 sm:text-sm">{im.title}</span>
                <span className="ml-3 shrink-0 text-[11px] font-medium tabular-nums text-slate-400">
                  {String(i + 1).padStart(2, '0')} / {String(n).padStart(2, '0')}
                </span>
              </div>
              <div className="flex min-h-0 flex-1 items-center justify-center px-3 pb-3 pt-1">
                <img
                  src={im.src}
                  alt={im.alt}
                  loading="eager"
                  className="max-h-full max-w-full rounded-lg bg-white object-contain shadow-lg ring-1 ring-slate-900/10"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
      {zoom && <Lightbox src={images[active].src} alt={images[active].alt} onClose={() => setZoom(false)} />}
      {n > 1 && (
        <div className="relative mt-6 flex justify-center gap-2">
          {images.map((im, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Show slide ${i + 1}: ${im.title}`}
              aria-current={i === active}
              onClick={() => onSelect?.(i)}
              className={`h-2 rounded-full transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${i === active ? 'w-6 bg-white' : 'w-2 bg-white/40 hover:bg-white/70'}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const NAV = 80; // height of the fixed header in px
const clamp = (v) => Math.min(1, Math.max(0, v));

// One feature row. On large screens it pins in place while scrolling steps through the slides;
// once the last slide is showing, the page scrolls on.
export const FeatureRow = ({ children, images, reverse = false }) => {
  const outerRef = useRef(null);
  const [scrollIdx, setScrollIdx] = useState(0);
  const [manual, setManual] = useState(null); // only used when the row is not pinned
  const [outerH, setOuterH] = useState(null); // null = not pinned
  const pinnedRef = useRef(false);
  const n = images.length;
  const active = manual ?? scrollIdx;

  // scrolling to a new step hands control back to the scroll position
  useEffect(() => { setManual(null); }, [scrollIdx]);

  // Dot click: when pinned, scroll to the spot where that slide is showing
  // (so scroll and slide stay in sync); otherwise just switch the slide.
  const goTo = (i) => {
    const el = outerRef.current;
    if (pinnedRef.current && el) {
      const vh = window.innerHeight || 800;
      const extra = vh * 0.2 * n;
      const targetTop = NAV - ((i + 0.5) / n) * extra;
      const delta = el.getBoundingClientRect().top - targetTop;
      window.scrollTo({ top: window.scrollY + delta, behavior: 'instant' });
    } else {
      setManual(i);
    }
  };

  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      const el = outerRef.current;
      if (!el) return;
      const vh = window.innerHeight || 800;
      const pin = n > 1 && window.innerWidth >= 1024 && vh >= 720;
      pinnedRef.current = pin;
      let idx = 0;
      if (pin) {
        const extra = vh * 0.2 * n;
        setOuterH(vh - NAV + extra);
        const p = clamp((NAV - el.getBoundingClientRect().top) / extra);
        idx = Math.min(n - 1, Math.floor(p * n));
      } else {
        setOuterH(null);
        if (n > 1) {
          const r = el.getBoundingClientRect();
          const tt = (vh - r.top) / (vh + r.height);
          idx = Math.min(n - 1, Math.floor(clamp((tt - 0.5) / 0.15) * n));
        }
      }
      setScrollIdx(idx);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [n]);

  const pinned = outerH !== null;
  return (
    <div ref={outerRef} style={pinned ? { height: outerH } : undefined}>
      <div
        className={pinned ? 'sticky flex items-center' : ''}
        style={pinned ? { top: NAV, height: `calc(100vh - ${NAV}px)` } : undefined}
      >
        <div
          className={`grid w-full grid-cols-1 items-center gap-10 lg:gap-14 ${reverse ? 'lg:grid-cols-[7fr_5fr]' : 'lg:grid-cols-[5fr_7fr]'}`}
        >
          <Reveal className={`min-w-0 ${reverse ? 'lg:order-2' : ''}`}>{children}</Reveal>
          <Reveal delay={150} className={`min-w-0 ${reverse ? 'lg:order-1' : ''}`}>
            <ScrollFrame images={images} active={active} onSelect={goTo} />
          </Reveal>
        </div>
      </div>
    </div>
  );
};